import {
  FormControl,
  FormDescription,
  FormField,
  type FormFieldProps,
  FormItem,
  FormLabel,
  FormMessage,
} from "@gigpro/ui/Form";
import { Label } from "@gigpro/ui/Label";
import { RadioGroup, RadioGroupItem } from "@gigpro/ui/RadioGroup";
import { cn } from "@gigpro/ui/tailwind/cn";
import { type TipPolicy, tipPolicySchema } from "api/resources/shared";
import React from "react";
import type { FieldPath, FieldValues } from "react-hook-form";

export const TIP_POLICY_QUESTION = "Does this shift include tips?";

/** The business's answers, in the order every tips question shows them. */
export const TIP_POLICY_OPTIONS: ReadonlyArray<{ value: TipPolicy; label: string }> = [
  { value: "INCLUDED", label: "Yes" },
  { value: "NOT_INCLUDED", label: "No" },
  { value: "POOLED", label: "Pooled" },
];

interface TipPolicyRadioGroupProps extends Pick<
  React.ComponentProps<typeof RadioGroup>,
  "aria-label" | "aria-labelledby" | "name" | "disabled" | "className"
> {
  /** `null` is unanswered: no option is selected. */
  value: TipPolicy | null | undefined;
  onValueChange: (tipPolicy: TipPolicy) => void;
  /** Prefix for the option ids, so each `<Label>` points at its own radio. */
  idPrefix: string;
}

/** The Yes / No / Pooled radios, shared by every place the tips question is asked. */
export function TipPolicyRadioGroup({
  value,
  onValueChange,
  idPrefix,
  className,
  ...props
}: Readonly<TipPolicyRadioGroupProps>) {
  return (
    <RadioGroup
      {...props}
      className={cn("flex gap-4", className)}
      value={value ?? ""}
      onValueChange={(next) => {
        const parsed = tipPolicySchema.safeParse(next);
        if (parsed.success) onValueChange(parsed.data);
      }}
    >
      {TIP_POLICY_OPTIONS.map(({ value: option, label }) => (
        <div key={option} className="flex items-center gap-1.5">
          <RadioGroupItem id={`${idPrefix}-${option}`} value={option} />
          <Label htmlFor={`${idPrefix}-${option}`} className="w-auto font-regular">
            {label}
          </Label>
        </div>
      ))}
    </RadioGroup>
  );
}

interface TipPolicyFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> extends Pick<
  FormFieldProps<TFieldValues, TName>,
  "control" | "name" | "className"
> {
  /** The selected skill's `tips_required`: shows the "Required" hint (the schema enforces it). */
  required?: boolean;
  disabled?: boolean;
}

/**
 * "Does this shift include tips?" Yes / No / Pooled (WORK-8452, WORK-9422). Radios rather than a switch
 * because unanswered (`null`) is a real state: optional for most skills, required when the skill's
 * `tips_required` is on.
 */
export function TipPolicyField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  className,
  required,
  disabled,
}: Readonly<TipPolicyFieldProps<TFieldValues, TName>>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn("flex flex-col gap-y-1.5", className)}>
          <FormLabel optional={!required}>{TIP_POLICY_QUESTION}</FormLabel>
          {required && <FormDescription className="text-gray">Required for this position</FormDescription>}
          <FormControl>
            <TipPolicyRadioGroup
              aria-label={TIP_POLICY_QUESTION}
              idPrefix={field.name}
              name={field.name}
              disabled={disabled}
              value={field.value}
              onValueChange={field.onChange}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
